import { useLocale } from '../i18n/LocaleProvider'
import { useEffect, useRef } from 'react'
import { ArrowDown, Brain, Code, Cpu, DeviceMobile, TShirt } from '@phosphor-icons/react'
import type { Icon } from '@phosphor-icons/react'
import { useInquiry } from './InquirySheet'
import { SiteButton } from './SiteButton'
import './Hero.css'

const supportedCategories: { icon: Icon; label: string }[] = [
  { icon: DeviceMobile, label: 'Apps' },
  { icon: Code, label: 'Software-based Services' },
  { icon: Brain, label: 'AI-related Services' },
  { icon: Cpu, label: 'Hardware' },
  { icon: TShirt, label: 'Fashion' },
]

function ScrollNumber({ value }: { value: number }) {
  return <span className="scroll-number">{value}</span>
}

function CategoryCarousel() {
  return (
    <div className="hero-category-carousel" aria-hidden="true">
      <div className="hero-category-track">
        {[...supportedCategories, ...supportedCategories].map(({ icon: CategoryIcon, label }, index) => (
          <span className="hero-category-cell" key={`${label}-${index}`}>
            <span className="hero-category-symbol">
              <CategoryIcon size={32} weight="light" />
            </span>
          </span>
        ))}
      </div>
    </div>
  )
}

export default function Hero() {
  const { locale, t } = useLocale()
  const { openInquiry } = useInquiry()
  const mediaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const media = mediaRef.current
    if (!media) return

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    let animationFrame = 0

    const updateParallax = () => {
      animationFrame = 0

      if (reduceMotion.matches) {
        media.style.setProperty('--hero-parallax-y', '0px')
        return
      }

      const rect = media.getBoundingClientRect()
      const distanceFromCenter = (window.innerHeight / 2) - (rect.top + rect.height / 2)
      const maxOffset = rect.height * 0.1
      const offset = Math.max(-maxOffset, Math.min(maxOffset, distanceFromCenter * 0.14))

      media.style.setProperty('--hero-parallax-y', `${offset.toFixed(2)}px`)
    }

    const requestParallaxUpdate = () => {
      if (animationFrame === 0) animationFrame = window.requestAnimationFrame(updateParallax)
    }

    const resizeObserver = new ResizeObserver(requestParallaxUpdate)
    resizeObserver.observe(media)
    window.addEventListener('scroll', requestParallaxUpdate, { passive: true })
    window.addEventListener('resize', requestParallaxUpdate)
    reduceMotion.addEventListener('change', requestParallaxUpdate)
    updateParallax()

    return () => {
      if (animationFrame !== 0) window.cancelAnimationFrame(animationFrame)
      resizeObserver.disconnect()
      window.removeEventListener('scroll', requestParallaxUpdate)
      window.removeEventListener('resize', requestParallaxUpdate)
      reduceMotion.removeEventListener('change', requestParallaxUpdate)
      media.style.removeProperty('--hero-parallax-y')
    }
  }, [])

  return (
    <section className="hero">
      <div className="hero-media" ref={mediaRef} aria-hidden="true">
        <img src="/home-hero-shibuya.jpg" alt="" />
      </div>

      <div className="hero-content">
        <div className="hero-text">
          <div className="hero-heading-row">
            <h1>
              {locale === 'ja' ? <>1億2,700万人の市場へ。<br />その一歩を私たちと。</> : <>
              <span className="hero-line-nowrap"><ScrollNumber value={127} />&nbsp;<span className="hero-million-full">million</span><span className="hero-million-short">mil</span> customers.</span><br />
              Invisible to you.<br />
              Not with us.</>}
            </h1>
            <CategoryCarousel />
          </div>
          <p className="hero-sub">
            {t('Join Notion, Framer, Cursor, and Perplexity — the smartest SaaS companies are just now moving in.')}
          </p>
          <div className="hero-actions">
            <SiteButton
              onClick={() => document.getElementById('plug-and-play')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            > {t("Enter Japan with Us")} <ArrowDown size={16} weight="bold" />
            </SiteButton>
            <SiteButton variant="light" onClick={() => openInquiry()}> {t("Talk to Sales")} </SiteButton>
          </div>
        </div>
      </div>
    </section>
  )
}
